import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, MapPin, Share2, MessageCircleHeart, ListTree, Send, X,
  AlertTriangle, Copy, Mail, Loader2, CalendarClock, ShieldAlert, QrCode,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import MultilingualText from "@/components/ui/MultilingualText";
import StatusBadge, { VerificationBadge } from "@/components/site/StatusBadge";
import { packMl, hasMlContent, mlPrimary } from "@/lib/ml";
import { fileUrl } from "@/lib/files";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

function Fact({ label, value, testid }) {
  if (value == null || value === "") return null;
  return (
    <div data-testid={testid}>
      <div className="overline text-[10px] text-[color:var(--ink-soft)]">{label}</div>
      <div className="mt-1 text-[15px]">{value}</div>
    </div>
  );
}

export default function MissingCase() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // tip form
  const [tipOpen, setTipOpen] = useState(false);
  const [tip, setTip] = useState({ body: "", when: "", where: "", contact: "" });
  const [sendingTip, setSendingTip] = useState(false);

  // community update
  const [updateBody, setUpdateBody] = useState("");
  const [postingUpdate, setPostingUpdate] = useState(false);

  // report abuse
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState({ kind: "incorrect", body: "" });
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/v1/missing/${caseId}`, { withCredentials: true });
      setData(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error("Could not open this case.");
    } finally {
      setLoading(false);
    }
  }, [caseId]);
  useEffect(() => {
    load();
  }, [load]);

  async function sendTip(e) {
    e.preventDefault();
    if (!tip.body.trim()) return;
    setSendingTip(true);
    try {
      const { data } = await axios.post(`${API}/v1/missing/${caseId}/tips`, tip);
      toast.success(data.message);
      setTip({ body: "", when: "", where: "", contact: "" });
      setTipOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send — please try again.");
    } finally {
      setSendingTip(false);
    }
  }

  async function postUpdate(e) {
    e.preventDefault();
    if (!hasMlContent(updateBody)) return;
    setPostingUpdate(true);
    try {
      await api.post(`/v1/missing/${caseId}/updates`, { kind: "community", body: packMl(updateBody) });
      setUpdateBody("");
      toast.success("Posted — labelled UNVERIFIED until the family or moderators confirm it.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPostingUpdate(false);
    }
  }

  async function sendReport(e) {
    e.preventDefault();
    if (!report.body.trim()) return;
    try {
      const { data } = await axios.post(`${API}/v1/missing/${caseId}/reports`, report);
      toast.success(data.message);
      setReport({ kind: "incorrect", body: "" });
      setReportOpen(false);
    } catch {
      toast.error("Could not send the report.");
    }
  }

  function shareUrl() {
    return window.location.href;
  }
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl());
      toast.success("Link copied — every share helps.");
    } catch {
      toast.error("Copy failed — select the address bar instead.");
    }
  }

  if (loading) {
    return <main className="pt-40 pb-32 text-center text-[color:var(--ink-soft)]">Opening the case file…</main>;
  }
  if (notFound || !data) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center">
        <h1 className="heritage-title text-4xl">This case is not public.</h1>
        <p className="mt-3 text-[color:var(--ink-soft)]">It may be private, withdrawn, or the link may be mistyped.</p>
        <Link to="/missing" className="btn-ghost mt-6 inline-flex" data-testid="case-back-directory">
          <ArrowLeft size={14} /> Back to the directory
        </Link>
      </main>
    );
  }

  const c = data.case;
  const updates = data.updates || [];
  const timeline = updates.filter((u) => u.kind === "timeline" || u.kind === "official");
  const community = updates.filter((u) => u.kind === "community");
  const place = [c.loc?.area, c.loc?.city, c.loc?.district, c.loc?.region, c.loc?.country].filter(Boolean).join(", ");

  return (
    <main data-testid="missing-case-page" className="pt-40 md:pt-48">
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-10">
        <Link to="/missing" className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline" data-testid="case-back">
          <ArrowLeft size={12} /> Missing &amp; Found
        </Link>

        <div className="mt-8 grid md:grid-cols-[300px_1fr] gap-10 items-start">
          {/* Portrait */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
            <div className="vintage-frame">
              <div className="aspect-[4/5] overflow-hidden bg-[color:var(--parchment-2)] flex items-center justify-center">
                {c.photo_path ? (
                  <img src={fileUrl(c.photo_path)} alt={c.name} className="w-full h-full object-cover" style={{ filter: "sepia(15%)" }} data-testid="case-photo" />
                ) : (
                  <span className="font-serif-display text-8xl text-[color:var(--sepia)]/40">{c.name?.[0]}</span>
                )}
              </div>
            </div>
            <div className="mt-3 numeric-index text-center text-[color:var(--ink-soft)]" data-testid="case-id">
              Case {c.case_id}
            </div>
          </motion.div>

          {/* Facts */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease }}>
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={c.status} testid="case-status" />
              {!c.reviewed && (
                <span className="border border-[color:var(--sepia)]/50 px-2 py-0.5 font-heritage-caps text-[10px] tracking-[0.14em] text-[color:var(--sepia)]" data-testid="case-pending-review">
                  Awaiting moderator review
                </span>
              )}
            </div>
            <h1 className="mt-4 heritage-title leading-tight" style={{ fontSize: "clamp(38px, 5vw, 76px)" }} data-testid="case-name">
              {c.name}
            </h1>
            {c.preferred_name && (
              <div className="mt-1 font-italic-serif italic text-lg text-[color:var(--ink-soft)]">known as {c.preferred_name}</div>
            )}

            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5 border-t border-b border-[color:var(--sepia)]/25 py-6">
              <Fact label="Last seen" value={c.last_seen_date} testid="case-last-seen" />
              <Fact label="Age" value={c.age != null ? `${c.age}` : null} testid="case-age" />
              <Fact label="Last known place" value={place} testid="case-place" />
              <Fact label="Languages" value={c.languages} />
              <Fact label="Gender" value={c.gender && c.gender !== "unknown" ? c.gender : null} />
              <Fact label="Clothing when last seen" value={c.clothing} />
              <Fact label="Vehicle" value={c.vehicle} />
              <Fact label="With" value={c.companions} />
            </div>

            {c.identifying && (
              <div className="mt-6">
                <div className="overline text-[10px] text-[color:var(--ink-soft)] mb-1">Identifying features</div>
                <MultilingualText value={c.identifying} className="text-[15px] leading-[1.6]" testid="case-identifying" />
              </div>
            )}
            {c.description && (
              <div className="mt-5">
                <div className="overline text-[10px] text-[color:var(--ink-soft)] mb-1">About them</div>
                <MultilingualText value={c.description} className="text-[16px] leading-[1.7] text-[color:var(--ink-soft)]" testid="case-description" />
              </div>
            )}
            {c.circumstances && (
              <div className="mt-5">
                <div className="overline text-[10px] text-[color:var(--ink-soft)] mb-1">Circumstances</div>
                <MultilingualText value={c.circumstances} className="text-[15px] leading-[1.65] text-[color:var(--ink-soft)]" testid="case-circumstances" />
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => setTipOpen(true)} data-testid="case-have-info" className="btn-rust">
                <MessageCircleHeart size={15} /> I have information
              </button>
              <button onClick={copyLink} data-testid="case-copy-link" className="btn-ghost">
                <Copy size={14} /> Copy link
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`MISSING — ${c.name}\nLast seen: ${c.last_seen_date}${place ? `, ${place}` : ""}\n${shareUrl()}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-share-whatsapp"
                className="btn-ghost"
              >
                <Share2 size={14} /> WhatsApp
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(`Missing: ${c.name} (${c.case_id})`)}&body=${encodeURIComponent(`Please help share this case: ${shareUrl()}`)}`}
                data-testid="case-share-email"
                className="btn-ghost"
              >
                <Mail size={14} /> Email
              </a>
              <button onClick={() => setQrOpen((v) => !v)} data-testid="case-qr-open" className="btn-ghost">
                <QrCode size={14} /> QR code
              </button>
            </div>

            {/* Printable QR — always opens the live case page */}
            {qrOpen && (
              <div className="mt-5 border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-5 max-w-sm" data-testid="case-qr-panel">
                <div className="flex items-start justify-between gap-3">
                  <div className="overline text-[10px] text-[color:var(--sepia)]">A poster that never goes stale</div>
                  <button onClick={() => setQrOpen(false)} className="p-1 hover:text-[color:var(--rust)]" data-testid="case-qr-close"><X size={13} /></button>
                </div>
                <img
                  src={`${API}/v1/missing/${c.case_id}/qr.png`}
                  alt={`QR code for case ${c.case_id}`}
                  className="mt-3 w-48 h-48 mx-auto border border-[color:var(--sepia)]/30"
                  data-testid="case-qr-image"
                />
                <p className="mt-3 text-xs text-[color:var(--ink-soft)] text-center leading-relaxed">
                  Print it on posters and community boards — it always opens this live page, however the case changes.
                </p>
                <div className="mt-3 flex justify-center gap-3">
                  <a
                    href={`${API}/v1/missing/${c.case_id}/qr.png`}
                    download={`${c.case_id}-qr.png`}
                    data-testid="case-qr-download"
                    className="btn-ghost !py-1.5 !px-3 !text-xs"
                  >
                    Download QR
                  </a>
                  <button onClick={() => window.print()} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="case-qr-print">
                    Print page
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={() => setReportOpen(true)}
              data-testid="case-report-open"
              className="mt-4 inline-flex items-center gap-1.5 text-xs link-underline text-[color:var(--ink-soft)]"
            >
              <ShieldAlert size={12} /> Report incorrect information, a scam, or abuse
            </button>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-12" data-testid="case-timeline">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
            <ListTree size={18} className="text-[color:var(--sepia)]" /> Case timeline
          </h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>
        <ol className="relative border-l-2 border-[color:var(--sepia)]/30 ml-3 space-y-8">
          {timeline.map((u, i) => (
            <li key={u.id} className="pl-6 relative" data-testid={`case-timeline-item-${i}`}>
              <span className="absolute -left-[7px] top-1.5 w-3 h-3 bg-[color:var(--parchment)] border-2 border-[color:var(--rust)] rounded-full" />
              <div className="flex flex-wrap items-center gap-2 numeric-index text-[color:var(--ink-soft)]">
                <CalendarClock size={11} />
                {u.created_at && new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
                <span>·</span>
                <span>{u.author_name}</span>
                <VerificationBadge verification={u.verification} />
              </div>
              <MultilingualText value={u.body} className="mt-1.5 text-[16px] leading-[1.6]" />
            </li>
          ))}
        </ol>
      </section>

      {/* Community updates */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-16" data-testid="case-community">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl">Community updates</h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>
        <p className="text-center text-sm text-[color:var(--ink-soft)] mb-8 max-w-xl mx-auto">
          Sightings and reports from the community appear as <strong>unverified</strong> until the family or moderators confirm them. To share something privately, use "I have information".
        </p>
        {community.length === 0 ? (
          <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)]" data-testid="case-community-empty">
            No community updates yet.
          </p>
        ) : (
          <ol className="space-y-5 max-w-3xl mx-auto">
            {community.map((u, i) => (
              <li key={u.id} className="border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-5" data-testid={`case-update-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{u.author_name}</span>
                  <VerificationBadge verification={u.verification} />
                  <span className="numeric-index text-[color:var(--ink-soft)]">
                    {u.created_at && new Date(u.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
                <MultilingualText value={u.body} className="mt-2 text-[15px] leading-[1.6]" />
              </li>
            ))}
          </ol>
        )}

        {user ? (
          <form onSubmit={postUpdate} className="max-w-3xl mx-auto mt-8" data-testid="case-update-form">
            <MultilingualEditor
              as="textarea"
              rows={2}
              testid="case-update-body"
              value={updateBody}
              onChange={setUpdateBody}
              maxLength={2000}
              placeholder="Share an update, a sighting, or a word of support…"
            />
            <button type="submit" data-testid="case-update-submit" disabled={postingUpdate || !hasMlContent(updateBody)} className="btn-wood mt-4 !py-2.5 disabled:opacity-50">
              <Send size={14} /> {postingUpdate ? "Posting…" : "Post to community"}
            </button>
          </form>
        ) : (
          <p className="text-center mt-8 text-sm text-[color:var(--ink-soft)]">
            <Link to="/app/login" className="link-underline text-[color:var(--rust)]">Sign in</Link> to post a community update — or use "I have information" to reach the family privately.
          </p>
        )}
      </section>

      {/* Disclaimer */}
      <section className="border-t border-[color:var(--sepia)]/20 bg-[color:var(--parchment-2)]/40">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 text-center">
          <AlertTriangle size={16} className="mx-auto text-[color:var(--sepia)]" />
          <p className="mt-3 text-xs leading-relaxed text-[color:var(--ink-soft)] max-w-2xl mx-auto">
            FamilyTree.Team is an information and community platform — not a police, forensic or identification authority.
            In an emergency or with time-critical information, always contact local police first.
            Locations shown are approximate. Identifications are never made from photographs or AI.
          </p>
        </div>
      </section>

      {/* Tip modal */}
      <AnimatePresence>
        {tipOpen && (
          <div className="fixed inset-0 bg-[color:var(--ink)]/60 backdrop-blur-sm flex items-center justify-center p-6" style={{ zIndex: 60 }} onClick={() => setTipOpen(false)}>
            <motion.form
              onSubmit={sendTip}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-lg w-full p-7 border border-[color:var(--sepia)]/40 shadow-[0_32px_80px_-20px_rgba(43,28,15,0.6)]"
              style={{ background: "var(--parchment)" }}
              data-testid="case-tip-modal"
            >
              <div className="flex items-start justify-between mb-1">
                <h2 className="heritage-title text-2xl">I have information</h2>
                <button type="button" onClick={() => setTipOpen(false)} className="p-1 hover:text-[color:var(--rust)]"><X size={16} /></button>
              </div>
              <p className="text-sm text-[color:var(--ink-soft)] mb-5">
                Your message goes <strong>privately</strong> to the family keeping this case — it is never published automatically.
              </p>
              <label className="block mb-4">
                <span className="overline block mb-1">What did you see or learn?</span>
                <textarea
                  data-testid="case-tip-body"
                  rows={3}
                  value={tip.body}
                  onChange={(e) => setTip({ ...tip, body: e.target.value })}
                  className="w-full bg-transparent border border-[color:var(--sepia)]/40 p-3 focus:outline-none focus:border-[color:var(--rust)] resize-none"
                  required
                />
              </label>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <label className="block">
                  <span className="overline block mb-1">When</span>
                  <input data-testid="case-tip-when" value={tip.when} onChange={(e) => setTip({ ...tip, when: e.target.value })} className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-1.5 focus:outline-none" />
                </label>
                <label className="block">
                  <span className="overline block mb-1">Where</span>
                  <input data-testid="case-tip-where" value={tip.where} onChange={(e) => setTip({ ...tip, where: e.target.value })} className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-1.5 focus:outline-none" />
                </label>
              </div>
              <label className="block mb-5">
                <span className="overline block mb-1">How the family can reach you (optional)</span>
                <input data-testid="case-tip-contact" value={tip.contact} onChange={(e) => setTip({ ...tip, contact: e.target.value })} className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-1.5 focus:outline-none" />
              </label>
              <button type="submit" data-testid="case-tip-submit" disabled={sendingTip || !tip.body.trim()} className="btn-rust w-full justify-center disabled:opacity-50">
                {sendingTip ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send privately
              </button>
            </motion.form>
          </div>
        )}
      </AnimatePresence>

      {/* Report modal */}
      <AnimatePresence>
        {reportOpen && (
          <div className="fixed inset-0 bg-[color:var(--ink)]/60 backdrop-blur-sm flex items-center justify-center p-6" style={{ zIndex: 60 }} onClick={() => setReportOpen(false)}>
            <motion.form
              onSubmit={sendReport}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-md w-full p-7 border border-[color:var(--sepia)]/40 shadow-[0_32px_80px_-20px_rgba(43,28,15,0.6)]"
              style={{ background: "var(--parchment)" }}
              data-testid="case-report-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <h2 className="heritage-title text-2xl">Report a concern</h2>
                <button type="button" onClick={() => setReportOpen(false)} className="p-1 hover:text-[color:var(--rust)]"><X size={16} /></button>
              </div>
              <select
                data-testid="case-report-kind"
                value={report.kind}
                onChange={(e) => setReport({ ...report, kind: e.target.value })}
                className="w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-2 mb-4 focus:outline-none"
              >
                <option value="incorrect">Incorrect information</option>
                <option value="scam">Suspected scam or fraud</option>
                <option value="fake_lead">Fake sighting or lead</option>
                <option value="impersonation">Impersonation</option>
                <option value="harassment">Harassment</option>
              </select>
              <textarea
                data-testid="case-report-body"
                rows={3}
                value={report.body}
                onChange={(e) => setReport({ ...report, body: e.target.value })}
                placeholder="Tell the moderators what you noticed…"
                className="w-full bg-transparent border border-[color:var(--sepia)]/40 p-3 focus:outline-none focus:border-[color:var(--rust)] resize-none"
                required
              />
              <button type="submit" data-testid="case-report-submit" disabled={!report.body.trim()} className="btn-wood w-full justify-center mt-4 disabled:opacity-50">
                <Send size={14} /> Send to moderators
              </button>
            </motion.form>
          </div>
        )}
      </AnimatePresence>
    </main>
  );
}
